import { useEffect, useState } from "react";
import styled from "styled-components";
import assets from "../../utils/ImagesPath";
import LS_SERVICE from "../../utils/localStorage";
import {
  LOGIN,
  VERIFY_OTP,
  SENT_OTP,
  IMAGE_BASE_URL,
} from "../../constants/api";
import { Form, Input, Spin, Button } from "antd";
import {
  UserOutlined,
  LockOutlined,
  MobileOutlined,
  SafetyOutlined,
  ArrowLeftOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { ERROR_MSG_TYPE, SUCCESS_MESSAGE } from "../../constants/messages";
import { postAPI, displayMessage } from "../../utils/common";
import {
  CREATOR_LINK,
  CREATOR_NAME,
  PROJECT_NAME,
} from "../../constants/constent";
import { Link } from "react-router-dom";

const Login = () => {
  const [companyName] = useState(PROJECT_NAME);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const [formType, setFormType] = useState("login"); // 'login' | 'forgot' | 'otp'
  const [loading, setLoading] = useState(false);
  const [mobile, setMobile] = useState(null);

  const logo = assets.logo;
  const images = assets.backgrounds;

  useEffect(() => {
    const interval = setInterval(changeBackgroundImage, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, images.length]);

  const changeBackgroundImage = () => {
    setVisible(false);
    setTimeout(() => {
      setCurrentIndex((currentIndex + 1) % images.length);
      setVisible(true);
    }, 1000); // 1 second fade-out
  };

  const handleForgotClick = () => {
    setFormType("forgot");
  };

  const handleOpenLink = () => {
    window.open(CREATOR_LINK, "_blank", "noopener,noreferrer");
  };
  const handleLoginFinish = (values) => {
    setLoading(true);
    const data = new FormData();
    data.append("email", values.username);
    data.append("password", values.password);
    data.append("user_type", "Admin");
    const successFn = function (data) {
      if (data["status"] === true) {
        if (data["user"]["user_type"] === "customer") {
          setLoading(false);
          displayMessage(
            ERROR_MSG_TYPE,
            "Customer accounts are restricted from accessing this portal.",
          );
        } else {
          let user = data.user;
          LS_SERVICE.set("id", user?.id);
          LS_SERVICE.set("name", user?.name);
          LS_SERVICE.set("user_type", user?.user_type);
          LS_SERVICE.set("phone", user?.phone);
          LS_SERVICE.set("email", user?.email);
          LS_SERVICE.set("token", data?.token);
          LS_SERVICE.set("vendor_id", user?.vendor?.id);
          LS_SERVICE.set("hotel_id", user?.vendor?.hotel_id);
          LS_SERVICE.set("referral_code", user?.referral_code);
          LS_SERVICE.set("employee_code", user?.employee_code);
          setLoading(false);
          displayMessage(
            SUCCESS_MESSAGE,
            data["message"] ?? "Login successful! Redirecting...",
          );
          window.location.reload();
        }
      } else {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, data["message"] ?? "Fail to Login");
      }
    };
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Fail to Login");
    };
    postAPI(LOGIN, data, successFn, errorFn);
  };

  const handleForgotFinish = (values) => {
    const { mobile } = values;
    if (mobile.length === 10) {
      setLoading(true);
      const data = new FormData();
      data.append("mobileNo", mobile);
      const successFn = function (data) {
        if (data["status"] === true) {
          setMobile(mobile);
          setFormType("otp");
          setLoading(false);
          displayMessage(SUCCESS_MESSAGE, data["message"]);
        } else {
          setLoading(false);
          displayMessage(ERROR_MSG_TYPE, data["message"]);
        }
      };
      const errorFn = function () {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Failed to send OTP.");
      };
      postAPI(SENT_OTP, data, successFn, errorFn);
    } else {
      displayMessage(ERROR_MSG_TYPE, "Enter valid 10-digit mobile number.");
    }
  };

  const handleOtpFinish = (values) => {
    const { otp } = values;
    if (otp.length === 4) {
      setLoading(true);
      const data = new FormData();
      data.append("mobileNo", mobile);
      data.append("otp", otp);
      const successFn = function (data) {
        if (data["status"] === true) {
          setLoading(false);
          setMobile(null);
          setFormType("login");
          displayMessage(SUCCESS_MESSAGE, data["message"]);
        } else {
          setLoading(false);
          displayMessage(ERROR_MSG_TYPE, data["message"]);
        }
      };
      const errorFn = function () {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "OTP verification failed.");
      };
      postAPI(VERIFY_OTP, data, successFn, errorFn);
    } else {
      displayMessage(ERROR_MSG_TYPE, "Enter valid 4-digit OTP.");
    }
  };

  const brandLogo = LS_SERVICE.get("setting")
    ? `${IMAGE_BASE_URL}${LS_SERVICE.get("setting")["logo"]}`
    : logo;
  const brandName = LS_SERVICE.get("setting")
    ? LS_SERVICE.get("setting")["company_name"]
    : companyName;

  const heading =
    formType === "login"
      ? "Sign in to your account"
      : formType === "forgot"
        ? "Reset your password"
        : "Verify your identity";

  const subheading =
    formType === "login"
      ? "Welcome back. Please enter your details to continue."
      : formType === "forgot"
        ? "Enter your registered mobile number to receive a verification code."
        : "Enter the 4-digit code sent to your registered contact.";

  return (
    <StyledWrapper>
      {/* ===================== LEFT BRAND PANEL ===================== */}
      <aside className="brand">
        <div
          className={`brand-bg ${visible ? "visible" : ""}`}
          style={{ backgroundImage: `url(${images[currentIndex]})` }}
        />
        <span className="shape shape-1" />
        <span className="shape shape-2" />

        <div className="brand-top">
          <img src={brandLogo} alt={brandName} className="brand-logo" />
        </div>

        <div className="brand-body">
          <span className="brand-eyebrow">MANAGEMENT PORTAL</span>
          <h1 className="brand-title">
            Everything you need, <br />
            in one secure place.
          </h1>
          <p className="brand-sub">
            Welcome to your {brandName} portal. Sign in to manage your account,
            billing and day-to-day operations with confidence.
          </p>

          <div className="brand-features">
            <div className="feat">
              <div className="feat-icon">
                <SafetyOutlined />
              </div>
              <div>
                <div className="feat-title">Enterprise-grade security</div>
                <div className="feat-desc">
                  Role-based access and encrypted sessions keep your data safe.
                </div>
              </div>
            </div>
            <div className="feat">
              <div className="feat-icon">
                <ThunderboltOutlined />
              </div>
              <div>
                <div className="feat-title">Smart billing &amp; automation</div>
                <div className="feat-desc">
                  Automated cycles, reminders and invoices — no manual work.
                </div>
              </div>
            </div>
            <div className="feat">
              <div className="feat-icon">
                <LineChartOutlined />
              </div>
              <div>
                <div className="feat-title">Real-time insights</div>
                <div className="feat-desc">
                  Track revenue, dues and performance at a glance.
                </div>
              </div>
            </div>
          </div>

          <div className="brand-stats">
            <div className="stat">
              <div className="stat-value">99.9%</div>
              <div className="stat-label">Uptime</div>
            </div>
            <div className="stat-divider" />
            <div className="stat">
              <div className="stat-value">24/7</div>
              <div className="stat-label">Support</div>
            </div>
            <div className="stat-divider" />
            <div className="stat">
              <div className="stat-value">100%</div>
              <div className="stat-label">Secure</div>
            </div>
          </div>
        </div>

        <div className="brand-foot">
          {brandName + " "}©{new Date().getFullYear()} · Powered by{" "}
          <b>
            <Link style={{ color: "orange" }} onClick={handleOpenLink} to="#">
              {CREATOR_NAME}
            </Link>
          </b>
        </div>
      </aside>

      {/* ===================== RIGHT FORM PANEL ===================== */}
      <main className="panel">
        {/* decorative elements */}
        <span className="p-blob p-blob-1" />
        <span className="p-blob p-blob-2" />
        <span
          className="p-watermark"
          aria-hidden
          style={{
            WebkitMaskImage: `url(${assets.fevicon})`,
            maskImage: `url(${assets.fevicon})`,
          }}
        />
        <span className="p-accent" />

        <div className="form-wrap">
          <div className="card-logo">
            <img src={brandLogo} alt={brandName} />
          </div>

          {formType !== "login" && (
            <button
              type="button"
              className="back-btn"
              onClick={() => setFormType("login")}
            >
              <ArrowLeftOutlined /> Back to login
            </button>
          )}

          <h2 className="heading">{heading}</h2>
          <p className="subheading">{subheading}</p>

          <Spin spinning={loading}>
            {formType === "login" && (
              <Form
                layout="vertical"
                className="form"
                onFinish={handleLoginFinish}
                requiredMark={false}
              >
                <Form.Item
                  name="username"
                  label="Username / Phone / Email"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your username/phone/email!",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    prefix={<UserOutlined className="in-icon" />}
                    placeholder="Enter your username, phone or email"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    { required: true, message: "Please enter your password" },
                  ]}
                >
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined className="in-icon" />}
                    placeholder="Enter your password"
                  />
                </Form.Item>

                <div className="row-end">
                  <span className="link" onClick={handleForgotClick}>
                    Forgot Password?
                  </span>
                </div>

                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="submit"
                  block
                  loading={loading}
                >
                  Sign In
                </Button>
              </Form>
            )}

            {formType === "forgot" && (
              <Form
                layout="vertical"
                className="form"
                onFinish={handleForgotFinish}
                requiredMark={false}
              >
                <Form.Item
                  name="mobile"
                  label="Mobile Number"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your mobile number",
                    },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Mobile number must be 10 digits",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    prefix={<MobileOutlined className="in-icon" />}
                    placeholder="Enter 10-digit mobile number"
                    maxLength={10}
                  />
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="submit"
                  block
                  loading={loading}
                >
                  Send OTP
                </Button>
              </Form>
            )}

            {formType === "otp" && (
              <Form
                layout="vertical"
                className="form"
                onFinish={handleOtpFinish}
                requiredMark={false}
              >
                <Form.Item name="mobile" label="Mobile Number">
                  <Input
                    size="large"
                    disabled
                    defaultValue={mobile}
                    prefix={<MobileOutlined className="in-icon" />}
                    placeholder="Mobile Number"
                  />
                </Form.Item>
                <Form.Item
                  name="otp"
                  label="Verification Code"
                  rules={[
                    { required: true, message: "Please enter the OTP" },
                    {
                      pattern: /^[0-9]{4}$/,
                      message: "OTP must be 4 digits",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    className="otp-input"
                    prefix={<SafetyOutlined className="in-icon" />}
                    placeholder="● ● ● ●"
                    maxLength={4}
                  />
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="submit"
                  block
                  loading={loading}
                >
                  Verify OTP
                </Button>
              </Form>
            )}
          </Spin>

          <p className="credit">
            {brandName + " "}©{new Date().getFullYear()} · Powered by{" "}
            <b>
              <Link style={{ color: "orange" }} onClick={handleOpenLink} to="#">
                {CREATOR_NAME}
              </Link>
            </b>
          </p>
        </div>
      </main>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  display: flex;
  font-family: "Segoe UI", Roboto, Helvetica, Arial, sans-serif;

  /* ===================== LEFT BRAND PANEL ===================== */
  .brand {
    position: relative;
    flex: 0 0 65%;
    max-width: 65%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 48px 56px;
    color: #fff;
    background: linear-gradient(
      155deg,
      color-mix(in srgb, var(--primary-color) 94%, #000) 0%,
      var(--primary-color) 55%,
      color-mix(in srgb, var(--primary-color) 78%, #000) 100%
    );
  }

  /* photo sits very subtly behind the gradient as texture */
  .brand-bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    opacity: 0;
    mix-blend-mode: overlay;
    transition: opacity 1.2s ease-in-out;
    z-index: 0;
  }
  .brand-bg.visible {
    opacity: 0.18;
  }

  .shape {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    z-index: 0;
  }
  .shape-1 {
    width: 320px;
    height: 320px;
    top: -120px;
    right: -90px;
  }
  .shape-2 {
    width: 200px;
    height: 200px;
    bottom: -70px;
    left: -60px;
    background: rgba(255, 255, 255, 0.06);
  }

  .brand-top,
  .brand-body,
  .brand-foot {
    position: relative;
    z-index: 1;
  }

  .brand-logo {
    height: 46px;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }

  .brand-body {
    margin-top: auto;
    margin-bottom: auto;
    max-width: 560px;
  }

  .brand-eyebrow {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 3px;
    padding: 6px 14px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.22);
    margin-bottom: 20px;
  }

  .brand-title {
    font-size: 42px;
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.5px;
    margin: 0 0 16px;
  }
  .brand-sub {
    font-size: 16px;
    line-height: 1.65;
    opacity: 0.9;
    margin: 0 0 36px;
  }

  .brand-features {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 40px;
  }
  .feat {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }
  .feat-icon {
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
  .feat-title {
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 3px;
  }
  .feat-desc {
    font-size: 13.5px;
    line-height: 1.5;
    opacity: 0.85;
    max-width: 420px;
  }

  .brand-stats {
    display: flex;
    align-items: center;
    gap: 28px;
    padding: 22px 26px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.16);
    width: fit-content;
  }
  .stat-value {
    font-size: 26px;
    font-weight: 800;
    line-height: 1;
  }
  .stat-label {
    font-size: 12.5px;
    opacity: 0.8;
    margin-top: 4px;
  }
  .stat-divider {
    width: 1px;
    align-self: stretch;
    background: rgba(255, 255, 255, 0.2);
  }

  .brand-foot {
    font-size: 13px;
    opacity: 0.8;
  }

  /* ===================== RIGHT FORM PANEL ===================== */
  .panel {
    position: relative;
    flex: 0 0 35%;
    max-width: 35%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    padding: 40px;
    overflow: hidden;
  }

  /* top accent strip in the brand color */
  .p-accent {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(
      90deg,
      var(--primary-color),
      color-mix(in srgb, var(--primary-color) 55%, #fff)
    );
    z-index: 1;
  }

  /* soft tinted blobs */
  .p-blob {
    position: absolute;
    border-radius: 50%;
    z-index: 0;
    filter: blur(10px);
  }
  .p-blob-1 {
    width: 240px;
    height: 240px;
    top: -90px;
    right: -80px;
    background: radial-gradient(
      circle,
      color-mix(in srgb, var(--primary-color) 16%, transparent) 0%,
      transparent 70%
    );
  }
  .p-blob-2 {
    width: 300px;
    height: 300px;
    bottom: -120px;
    left: -110px;
    background: radial-gradient(
      circle,
      color-mix(in srgb, var(--primary-color) 12%, transparent) 0%,
      transparent 70%
    );
  }

  /* favicon watermark (white texture) tinted with the brand color */
  /* favicon rendered via mask so its shape is filled with the brand color
     (independent of the image's own colors), with a soft primary glow */
  .p-watermark {
    position: absolute;
    bottom: -30px;
    right: -30px;
    width: 240px;
    height: 240px;
    background-color: var(--primary-color);
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-size: contain;
    mask-size: contain;
    opacity: 0.14;
    filter: drop-shadow(
      0 0 20px color-mix(in srgb, var(--primary-color) 55%, transparent)
    );
    transform: rotate(-12deg);
    z-index: 0;
    pointer-events: none;
  }

  .form-wrap {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 400px;
    box-sizing: border-box;
  }

  .card-logo {
    display: none;
    justify-content: center;
    margin-bottom: 20px;
  }
  .card-logo img {
    height: 46px;
    object-fit: contain;
  }

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: 0;
    padding: 0;
    margin-bottom: 14px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color);
    cursor: pointer;
  }
  .back-btn:hover {
    opacity: 0.8;
  }

  .heading {
    font-size: 24px;
    font-weight: 800;
    color: #111827;
    margin: 0 0 6px;
  }
  .subheading {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 26px;
    line-height: 1.5;
  }

  .form {
    width: 100%;
  }
  .form .ant-form-item {
    margin-bottom: 18px;
  }
  .form .ant-form-item-label {
    padding-bottom: 5px;
  }
  .form .ant-form-item-label > label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    height: auto;
  }

  /* unified field styling (text, password, otp affix wrappers) */
  .ant-input-affix-wrapper,
  .ant-input {
    border-radius: 10px;
    border: 1px solid #e3e6ee;
    background: #fbfbfd;
    box-shadow: none;
  }
  .ant-input-affix-wrapper {
    padding: 11px 14px;
  }
  .ant-input-affix-wrapper:hover,
  .ant-input:hover {
    border-color: var(--primary-color);
  }
  .ant-input-affix-wrapper-focused,
  .ant-input-affix-wrapper:focus-within,
  .ant-input:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px
      color-mix(in srgb, var(--primary-color) 16%, transparent);
  }
  .ant-input-affix-wrapper > .ant-input {
    border: 0;
    background: transparent;
    padding: 0;
    font-size: 15px;
  }
  .in-icon {
    color: #9aa3af;
    font-size: 16px;
    margin-right: 4px;
  }
  .otp-input input {
    letter-spacing: 8px;
    font-weight: 700;
    text-align: center;
  }

  .row-end {
    display: flex;
    justify-content: flex-end;
    margin: -6px 0 20px;
  }
  .link {
    cursor: pointer;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--primary-color);
  }
  .link:hover {
    opacity: 0.8;
  }

  .submit {
    height: 48px;
    border: 0 !important;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    background: var(--primary-color);
    box-shadow: 0 8px 20px
      color-mix(in srgb, var(--primary-color) 32%, transparent);
    transition: transform 0.1s ease;
  }
  .submit:hover {
    transform: translateY(-1px);
  }
  .submit:active {
    transform: translateY(0);
  }

  .credit {
    margin: 22px 0 0;
    text-align: center;
    font-size: 12.5px;
    color: #9aa3af;
  }
  .credit a {
    color: var(--primary-color);
    font-weight: 600;
  }

  /* ===================== RESPONSIVE ===================== */
  @media (max-width: 900px) {
    .brand {
      display: none;
    }
    .panel {
      flex: 1 1 100%;
      max-width: 100%;
    }
    .card-logo {
      display: flex;
    }
  }
`;

export default Login;
